import { buildTeardropSvgMarkup, parseMemberIconRef, getMemberIconPublicUrl, WATER_DROP_STATES } from '../core';

/**
 * DOM 水滴标记工厂（地图 Marker 用，不依赖 React）
 * 指挥页/多项目只依赖 core + 本文件
 *
 * 硬约束（PITFALLS 6.1）：Mapbox HTML Marker 根节点禁止写
 * position / transform —— 会覆盖 .mapboxgl-marker 的 absolute+transform，缩放时锚点必漂。
 * 角标定位只放在「内部包装节点」或直接画进 SVG。
 */
function applyPinBox(el, width, height, title) {
  el.style.width = `${width}px`;
  el.style.height = `${height}px`;
  el.style.margin = '0';
  el.style.padding = '0';
  el.style.border = 'none';
  el.style.background = 'transparent';
  el.style.cursor = 'pointer';
  el.style.lineHeight = '0';
  // 根节点绝不设 position/transform，交给 Mapbox
  el.style.position = '';
  el.style.transform = '';
  el.title = title || '';
}

function normalizeCornerBadge(cornerBadge, fromApp) {
  if (cornerBadge === 'app' || cornerBadge === 'lc') return cornerBadge;
  if (fromApp) return 'app';
  return null;
}

/** 在内部 relative 包装层上叠 app/lc 小方块文字角标（勿写在 Marker 根上） */
function appendCornerBadgeOverlay(hostEl, cornerBadge) {
  if (!cornerBadge || !hostEl) return;
  hostEl.style.overflow = 'visible';
  const badge = document.createElement('span');
  badge.className = 'zhxt-member-corner-badge';
  badge.textContent = cornerBadge;
  badge.style.position = 'absolute';
  badge.style.top = '0';
  badge.style.right = '0';
  badge.style.minWidth = '15px';
  badge.style.height = '11px';
  badge.style.padding = '0 2px';
  badge.style.borderRadius = '2.5px';
  badge.style.fontSize = '8px';
  badge.style.fontWeight = '700';
  badge.style.lineHeight = '11px';
  badge.style.textAlign = 'center';
  badge.style.color = '#fff';
  badge.style.fontFamily = 'system-ui,sans-serif';
  badge.style.pointerEvents = 'none';
  // 与单兵工具一致：app 青绿底、lc 蓝底，白字小方块
  badge.style.background = cornerBadge === 'app' ? '#0f766e' : '#1d4ed8';
  hostEl.appendChild(badge);
}

function createChromeFilePin(iconKey, title, width, height, cornerBadge = null) {
  const el = document.createElement('div');
  applyPinBox(el, width, height, title);

  // 角标需要定位上下文时，只用「内层」relative，根节点保持可被 Mapbox 接管
  const host = document.createElement('div');
  host.style.width = `${width}px`;
  host.style.height = `${height}px`;
  host.style.margin = '0';
  host.style.padding = '0';
  host.style.lineHeight = '0';
  host.style.overflow = 'visible';
  if (cornerBadge) {
    host.style.position = 'relative';
  }

  const img = document.createElement('img');
  img.src = getMemberIconPublicUrl(iconKey);
  img.alt = title || '';
  img.draggable = false;
  img.style.width = `${width}px`;
  img.style.height = `${height}px`;
  img.style.objectFit = 'contain';
  img.style.display = 'block';
  img.style.pointerEvents = 'none';
  host.appendChild(img);
  appendCornerBadgeOverlay(host, cornerBadge);
  el.appendChild(host);
  return el;
}

export function createColorPinElement(pinColor, title, iconKey = 'water_drop', cornerBadge = null) {
  const parsed = parseMemberIconRef(iconKey);
  const fill = pinColor || parsed.fill || '#8a93a0';
  const officialFills = new Set((WATER_DROP_STATES || []).map((s) => s.fill));
  const badge = normalizeCornerBadge(cornerBadge, false);
  const w = badge ? 34 : 28;
  const h = badge ? 40 : 36;

  // 有 app/lc 角标时优先走 SVG（角标画进 SVG，根节点无 position），避免 HTML overlay 再次踩坑
  if (badge) {
    const el = document.createElement('div');
    applyPinBox(el, w, h, title);
    el.innerHTML = buildTeardropSvgMarkup({
      fill,
      iconKey,
      style: parsed.style || 'chrome',
      cornerBadge: badge
    }).replace('<svg ', '<svg style="display:block;pointer-events:none" ');
    return el;
  }

  if (parsed.style === 'chrome' && parsed.key === 'water_drop' && officialFills.has(fill)) {
    return createChromeFilePin(iconKey, title, w, h, null);
  }
  const el = document.createElement('div');
  applyPinBox(el, w, h, title);
  el.innerHTML = buildTeardropSvgMarkup({
    fill,
    iconKey,
    style: parsed.style || 'chrome',
    cornerBadge: null
  }).replace('<svg ', '<svg style="display:block;pointer-events:none" ');
  return el;
}

export function createAppMarkerElement(pinColor, title, iconKey = 'water_drop') {
  return createColorPinElement(pinColor, title || '单兵 App', iconKey, 'app');
}

export function createLocusMarkerBadgeElement(pinColor, title, iconKey = 'water_drop') {
  return createColorPinElement(pinColor, title || 'Locus', iconKey, 'lc');
}

export function createTeardropMarkerElement({
  pinColor,
  title,
  iconKey = 'chrome:water_drop',
  fromApp = false,
  cornerBadge = null
} = {}) {
  // 单兵/独立 App → app；Locus → lc（小方块文字角标，不要箭头图）
  const badge = normalizeCornerBadge(cornerBadge, fromApp) || (fromApp ? 'app' : 'lc');
  return createColorPinElement(pinColor, title, iconKey, badge);
}
